import { useMutation } from '@apollo/client';
import {
  CompanyUpdateInput,
  CustomerOnboardingState,
  SocialLinkCreateWithoutCompanyInput,
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
  UserUpdateInput,
} from 'lib/generated/graphql';
import { UPDATE_COMPANY_MUTATION } from 'lib/mutations';
import { FIND_HUDDLES, FIND_REQUESTS_QUERY, GET_VIEWER } from 'lib/queries';
import React, { FunctionComponent, useRef, useState } from 'react';
import Video from 'src/screens/join/components/OnboardingFounder/FormStep3';
import Carousel from 'src/screens/onboarding/components/Carousel';
import CompanyForm from 'src/screens/user/components/CustomerCompleteProfile/Company';
import User from 'src/screens/user/components/CustomerCompleteProfile/User';
import useAuth from 'src/shared-components/Auth';
import FormTags from 'src/shared-components/forms/Tags';

const steps = [
  {
    position: 0,
    title: 'Your Company Profile',
  },
  {
    position: 1,
    title: 'Add a few Company Tags',
    subtitle: 'These help match your company and its Huddles to builders in the community.',
    action: 'Next',
  },
  {
    position: 2,
    title: 'Tell the full story ✨',
    subtitle: 'We’d love to get to know you and more about  your story and where you’re heading.',
    action: 'Next',
  },
  {
    position: 3,
    title: 'Your user profile',
  },
];

interface CustomerCompleteProps {
  setIsLoading?: (isLoading: boolean) => void;
  closeModal?: () => void;
}

const CustomerComplete: FunctionComponent<CustomerCompleteProps> = ({
  setIsLoading,
  closeModal,
}: CustomerCompleteProps) => {
  // states
  const { user, update: updateUser } = useAuth();

  const company = user?.companies[0];
  const [updateCompanyMutation] = useMutation<UpdateCompanyMutation, UpdateCompanyMutationVariables>(
    UPDATE_COMPANY_MUTATION,
    { refetchQueries: [GET_VIEWER, FIND_REQUESTS_QUERY, FIND_HUDDLES] },
  );
  const childForms = useRef<any>([]);
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const next = () => {
    if (currentStep.position < steps.length - 1) setCurrentStep(steps[currentStep.position + 1]);
  };

  const stepperButtonFunctions = () => {
    (childForms.current[currentStep.position] as any).submitForm();
  };

  const completeUserForm = async (bio?: string, twitterUsername?: string, linkedInLink?: string, photoUrl?: string) => {
    const socialLinksData: SocialLinkCreateWithoutCompanyInput[] = [];
    if ((twitterUsername && twitterUsername.length) || (linkedInLink && linkedInLink.length)) {
      if (linkedInLink && linkedInLink.length) {
        socialLinksData.push({
          platformName: 'LinkedIn',
          link: linkedInLink,
        });
      }

      if (twitterUsername && twitterUsername.length) {
        socialLinksData.push({
          platformName: 'Twitter',
          username: twitterUsername,
          link: `https://twitter.com/${twitterUsername}`,
        });
      }
    }

    const updateData: UserUpdateInput = {
      bio,
      photoUrl,
      socialLinks: {
        createMany: {
          data: socialLinksData,
        },
      },
    };
    await updateUser({
      ...updateData,
    });
    if (closeModal) closeModal();
  };

  const updateCompany = async (updateCompanyData: CompanyUpdateInput) => {
    try {
      if (setIsLoading) {
        setIsLoading(true);
      }
      await updateCompanyMutation({
        variables: {
          updateCompanyWhere: {
            id: company.id,
          },
          updateCompanyData,
        },
      });
      if (setIsLoading) {
        setIsLoading(false);
      }
      next();
    } catch (error) {
      console.log(error);
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  const uploadVideo = async (videoUrl: string, pitchDeckUrl: string) => {
    await updateCompany({
      videoUrl: videoUrl,
      pitchDeckUrl,
    });
    next();
  };

  const moveToFirstProject = async () => {
    updateUser({
      customerOnboarding: {
        update: {
          state: {
            set: CustomerOnboardingState.FirstProject,
          },
        },
      },
    });
    global?.analytics.track('Completed Huddle Request');
  };

  return (
    <Carousel
      steps={steps}
      currentStep={currentStep.position}
      onButtonClick={stepperButtonFunctions}
      className="w-[400px]"
      titleClassName="text-left mt-5"
      textCenterTitle={false}
      stepTextCenter={false}
      subtitleClass="text-left w-11/12"
      buttonColor="black"
      buttonSize="xl"
      buttonClass="bg-[#151515]"
    >
      {currentStep.position === 0 && (
        <div>
          <CompanyForm
            onFormSubmitted={companyUpdateInput => {
              global?.analytics.track('Completed Company Information Step');
              updateCompany(companyUpdateInput);
            }}
            buttonText="Next - Add Company Tags"
          />
        </div>
      )}
      {currentStep.position === 1 && (
        <div>
          <FormTags
            type="COMPANY"
            ref={el => (childForms.current[1] = el)}
            selectedIds={company?.tags?.map(tag => tag.id)}
            onFormSubmitted={tagIds => {
              updateCompany({ tags: { set: tagIds.map((tagId: number) => ({ id: tagId })) } });
              global?.analytics.track('Completed Company Tags Step');
            }}
          />
        </div>
      )}
      {currentStep.position === 2 && (
        <div>
          <Video
            ref={el => (childForms.current[2] = el)}
            onFormSubmitted={({ videoUrl, pitchDeckUrl }) => {
              uploadVideo(videoUrl, pitchDeckUrl);
              global?.analytics.track('Completed Company Video Step');
            }}
            button={false}
          />
        </div>
      )}
      {currentStep.position === 3 && (
        <div>
          <User
            onFormSubmitted={({ bio, twitterUsername, linkedInLink, photoUrl }) => {
              completeUserForm(bio, twitterUsername, linkedInLink, photoUrl);
              global?.analytics.track('Completed User Information Step', {
                bio,
                twitterUsername,
                linkedInLink,
                photoUrl,
              });
              moveToFirstProject();
            }}
          />
        </div>
      )}
    </Carousel>
  );
};

export default CustomerComplete;
